import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query';
import type { Reactive } from 'vue';
import type {
  UseProductReviews,
  UseProductReviewsReturn,
  ProductReviewsQueryData,
  AddProductReviewParams,
  UpdateProductReviewParams,
  DeleteProductReviewParams,
  FetchProductReviews,
  AddProductReview,
  UpdateProductReview,
  DeleteProductReview,
} from '~/composables/useProductReviews/types';
import { useSdk } from '~/sdk';
import { useProductParams } from '~/composables/useProductParams';
import type { CreateProductReviewRequest, UpdateProductReviewRequest } from '@vue-storefront/prestashop-api';


/**
 * @description Composable managing product reviews data using TanStack Query
 * @param {MaybeRef<number | string> | undefined} productId Optional product ID (can be provided later)
 * @returns {@link UseProductReviewsReturn}
 * @example
 * const { query, mutationAdd, reviews, summary } = useProductReviews(123);
 */
export const useProductReviews: UseProductReviews = (productId) => {
  const queryClient = useQueryClient();
  const sdk = useSdk();
  const { id } = useProductParams();

  // Get the current product ID, defaulting to the provided productId or the route parameter
  const currentProductId = computed(() => {
    return unref(productId) || id;
  });

  // Query for fetching product reviews
  const query = useQuery({
    queryKey: computed(() => ['productReviews', currentProductId.value]),
    queryFn: async (): Promise<ProductReviewsQueryData> => {
      const productIdValue = currentProductId.value;
      if (!productIdValue) {
        throw new Error('Product ID is required');
      }
      return await sdk.commerce.getProductReviews({ productId: productIdValue });
    },
    enabled: computed(() => !!currentProductId.value),
  });
  // Mutation for adding a product review
  const mutationAdd = useMutation({
    mutationFn: async (params: AddProductReviewParams) => {
      const productIdValue = unref(params.productId);
      const review = unref(params.review);

      // Check if user can review this product
      if (!query.data.value?.can_review) {
        throw new Error('You must purchase this product before you can leave a review');
      }

      // Validate review data
      if (!review.title || !review.content || !review.rating) {
        throw new Error('Review title, content and rating are required');
      }

      // Validate lengths
      if (review.title.length < 3) {
        throw new Error('Review title must be at least 3 characters');
      }

      if (review.content.length < 10) {
        throw new Error('Review content must be at least 10 characters');
      }

      console.log('Adding product review:', { productId: productIdValue, review });
      const result = await sdk.commerce.createProductReview({ 
        productId: productIdValue, 
        review: {
          title: review.title,
          content: review.content,
          rating: review.rating
        } 
      });
      console.log('Product review API response:', result);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['productReviews'] });
    },
    onError: (error) => {
      console.error('Error adding product review:', error);
      useHandleError(error instanceof Error ? error : new Error('Failed to add review'));
    },
  });
  // Mutation for updating a product review
  const mutationUpdate = useMutation({
    mutationFn: async (params: UpdateProductReviewParams) => {
      const productIdValue = unref(params.productId);
      const reviewIdValue = unref(params.reviewId);
      const review = unref(params.review);

      if (!productIdValue || !reviewIdValue) {
        throw new Error('Product ID and Review ID are required');
      }

      // Check if user can review this product
      if (!query.data.value?.can_review) {
        throw new Error('You must purchase this product before you can leave a review');
      }

      // Validate review data
      if (!review.title || !review.content || !review.rating) {
        throw new Error('Review title, content and rating are required');
      }

      // Validate lengths
      if (review.title.length < 3) {
        throw new Error('Review title must be at least 3 characters');
      }

      if (review.content.length < 10) {
        throw new Error('Review content must be at least 10 characters');
      }

      console.log('Updating product review:', { productId: productIdValue, reviewId: reviewIdValue, review });
      const result = await sdk.commerce.updateProductReview({ 
        productId: productIdValue, 
        reviewId: reviewIdValue, 
        review: {
          title: review.title,
          content: review.content,
          rating: review.rating
        } 
      });
      console.log('Product review update API response:', result);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['productReviews'] });
    },
    onError: (error) => {
      console.error('Error updating product review:', error);
      useHandleError(error instanceof Error ? error : new Error('Failed to update review'));
    },
  });

  // Mutation for deleting a product review
  const mutationDelete = useMutation({
    mutationFn: async (params: DeleteProductReviewParams) => {
      const productIdValue = unref(params.productId);
      const reviewIdValue = unref(params.reviewId);

      if (!productIdValue || !reviewIdValue) {
        throw new Error('Product ID and Review ID are required');
      }

      console.log('Deleting product review:', { productId: productIdValue, reviewId: reviewIdValue });
      const result = await sdk.commerce.deleteProductReview({ 
        productId: productIdValue, 
        reviewId: reviewIdValue 
      });
      console.log('Product review delete API response:', result);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['productReviews'] });
    },
    onError: (error) => {
      console.error('Error deleting product review:', error);
      useHandleError(error instanceof Error ? error : new Error('Failed to delete review'));
    },
  });
  // Computed properties for easy access to data
  const reviews = computed(() => {
    return query.data.value?.reviews || [];
  });

  const summary = computed(() => {
    return query.data.value || null;
  });

  const canReview = computed(() => {
    return query.data.value?.can_review ?? false;
  });

  const data = computed(() => {
    // For backward compatibility, map reviews to SfProductReview format
    return query.data.value?.reviews?.map(review => ({
      id: review.id.toString(),
      reviewer: review.author,
      rating: review.rating,
      createdAt: review.date,
      text: review.content,
      title: review.title,
    })) || null;
  });

  // Legacy compatibility methods
  const fetchProductReviews: FetchProductReviews = async (productIdParam) => {
    const targetProductId = productIdParam || currentProductId.value;
    if (!targetProductId) {
      throw new Error('Product ID is required');
    }
    
    // Update the reactive productId if different
    if (productId && typeof productId === 'object' && 'value' in productId) {
      productId.value = targetProductId;
    }
    
    await query.refetch();
  };
  const addProductReview: AddProductReview = async (productIdParam, review) => {
    const targetProductId = productIdParam || currentProductId.value;
    if (!targetProductId) {
      throw new Error('Product ID is required');
    }

    // Check if user can review this product
    if (!canReview.value) {
      throw new Error('You must purchase this product before you can leave a review');
    }

    try {
      const result = await mutationAdd.mutateAsync({
        productId: targetProductId,
        review
      });

      // Check if the result has success property (from data.psdata)
      const success = result && typeof result === 'object' &&
                     'success' in result && result.success === true;

      return success;
    } catch (error) {
      return false;
    }
  };

  const updateProductReview: UpdateProductReview = async (productIdParam, reviewId, review) => {
    const targetProductId = productIdParam || currentProductId.value;
    if (!targetProductId || !reviewId) {
      throw new Error('Product ID and Review ID are required');
    }

    try {
      const result = await mutationUpdate.mutateAsync({
        productId: targetProductId,
        reviewId,
        review
      });

      // Check if the result has message/review properties (indicating success)
      const success = result && typeof result === 'object' &&
                     ('message' in result || 'review' in result);

      return success;
    } catch (error) {
      return false;
    }
  };

  const deleteProductReview: DeleteProductReview = async (productIdParam, reviewId) => {
    const targetProductId = productIdParam || currentProductId.value;
    if (!targetProductId || !reviewId) {
      throw new Error('Product ID and Review ID are required');
    }

    try {
      const result = await mutationDelete.mutateAsync({
        productId: targetProductId,
        reviewId
      });

      // Check if the result has message property (indicating success)
      const success = result && typeof result === 'object' && 'message' in result;

      return success;
    } catch (error) {
      return false;
    }
  };
  return {
    // TanStack Query objects
    query: reactive(query) satisfies Reactive<typeof query>,
    mutationAdd: reactive(mutationAdd) satisfies Reactive<typeof mutationAdd>,
    mutationUpdate: reactive(mutationUpdate) satisfies Reactive<typeof mutationUpdate>,
    mutationDelete: reactive(mutationDelete) satisfies Reactive<typeof mutationDelete>,
    
    // Computed properties for easy access
    reviews,
    summary,
    data,
    canReview,
    
    // Legacy compatibility methods
    fetchProductReviews,
    addProductReview,
    updateProductReview,
    deleteProductReview,
  };
};
